import React, { useEffect, useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { DryBulkOption } from "../hooks/useDryBulkReferenceOptions";
import {
  getCargoLineSchema,
  LIMITS,
  OPERATION_TYPES,
  type CargoLine,
  type CargoLineFormData,
} from "../schema/dryBulkNominationSchema";
import DryBulkDrawerFields from "./DryBulkDrawerFields";

const LANG_BASE = "vessel-nomination.dry-bulk.add.cargo-lines";
const EMPTY: CargoLineFormData = {
  operationType: "" as CargoLineFormData["operationType"],
  cargoCode: "",
  quantityMt: "" as unknown as number,
  shipperCompanyCode: "",
  stockPileCode: "",
  receiverCompanyCode: "",
};

interface Props {
  isOpen: boolean;
  line?: CargoLine;
  cargoOptions: DryBulkOption[];
  shipperOptions: DryBulkOption[];
  receiverOptions: DryBulkOption[];
  stockPileOptions: DryBulkOption[];
  onClose: () => void;
  onSave: (line: CargoLine) => void;
}

/** VN-06: a Loading line needs Shipper and S. Pile, an Unloading line a Receiver (VD-47); added only when valid (G-2). */
const CargoLineDrawer: React.FC<Props> = ({
  isOpen,
  line,
  cargoOptions,
  shipperOptions,
  receiverOptions,
  stockPileOptions,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const schema = useMemo(() => getCargoLineSchema(t), [t]);
  const form = useForm<CargoLineFormData, undefined, CargoLine>({
    mode: "onTouched",
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (isOpen) form.reset(line ? { ...EMPTY, ...line } : EMPTY);
  }, [form, isOpen, line]);

  const operationType = form.watch("operationType");
  const operationOptions = OPERATION_TYPES.map((value) => ({
    value,
    label: t(`${LANG_BASE}.operation-types.${value}`),
  }));

  return (
    <div data-testid="dry-bulk-cargo-line-drawer">
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={form.handleSubmit((values) => onSave(values))}
          title={t(`${LANG_BASE}.${line ? "edit" : "add"}`)}
          submitLabel={t(`${LANG_BASE}.${line ? "save" : "add-submit"}`)}
          cancelLabel={t("common.cancel")}
        >
          <DryBulkDrawerFields testId="dry-bulk-cargo-line-fields">
            <FormItem<CargoLineFormData, "operationType">
              required
              testId="dry-bulk-cargo-line-operation-type"
              name="operationType"
              type="select"
              label={t(`${LANG_BASE}.operation-type`)}
              options={operationOptions}
            />
            <FormItem<CargoLineFormData, "cargoCode">
              required
              testId="dry-bulk-cargo-line-cargo-code"
              name="cargoCode"
              type="autocomplete"
              label={t(`${LANG_BASE}.cargo-code`)}
              options={cargoOptions}
            />
            <FormItem<CargoLineFormData, "quantityMt">
              required
              testId="dry-bulk-cargo-line-quantity"
              name="quantityMt"
              type="number"
              label={t(`${LANG_BASE}.quantity`)}
              adornment={t(`${LANG_BASE}.unit-mt`)}
              maxDigits={LIMITS.QUANTITY_DIGITS}
              decimals={0}
            />
            {operationType === "LOADING" && (
              <>
                <FormItem<CargoLineFormData, "shipperCompanyCode">
                  required
                  testId="dry-bulk-cargo-line-shipper"
                  name="shipperCompanyCode"
                  type="autocomplete"
                  label={t(`${LANG_BASE}.shipper`)}
                  options={shipperOptions}
                />
                <FormItem<CargoLineFormData, "stockPileCode">
                  required
                  testId="dry-bulk-cargo-line-stock-pile"
                  name="stockPileCode"
                  type="autocomplete"
                  label={t(`${LANG_BASE}.stock-pile`)}
                  options={stockPileOptions}
                />
              </>
            )}
            {operationType === "DISCHARGE" && (
              <FormItem<CargoLineFormData, "receiverCompanyCode">
                required
                testId="dry-bulk-cargo-line-receiver"
                name="receiverCompanyCode"
                type="autocomplete"
                label={t(`${LANG_BASE}.receiver`)}
                options={receiverOptions}
              />
            )}
          </DryBulkDrawerFields>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default CargoLineDrawer;
